import React, { useEffect, useState } from 'react';
import Input from '../../../../shared/components/Input/Input';
import Select from '../../../../shared/components/Select/Select';
import Checkbox from '../../../../shared/components/Checkbox/Checkbox';
import Button from '../../../../shared/components/Button/Button';
import Typography from '../../../../shared/components/Typography/Typography';
import Alert from '../../../../shared/components/Alert/Alert';
import {
  getPartnerQuoteSettings,
  updatePartnerQuoteSettings,
} from '../partner.api';
import {
  EMPTY_PARTNER_QUOTE_SETTINGS,
  normalizePartnerQuoteSettings,
} from '../partnerQuoteSettingsModel';

const coefficientFields = [
  ['laborCoefficient', 'Coefficient main-d’œuvre'],
  ['materialCoefficient', 'Coefficient matériel'],
  ['subcontractingCoefficient', 'Coefficient sous-traitance'],
  ['coContractingCoefficient', 'Coefficient co-traitance'],
];

export default function PartnerQuoteSettingsPanel({ partner }) {
  const [settings, setSettings] = useState(EMPTY_PARTNER_QUOTE_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    getPartnerQuoteSettings(partner.id)
      .then((value) => { if (active) setSettings(normalizePartnerQuoteSettings(value)); })
      .catch((requestError) => { if (active) setError(requestError.message || 'Impossible de charger les conditions de devis.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [partner.id]);

  const update = (field, value) => {
    setSuccess('');
    setSettings((current) => ({ ...current, [field]: value }));
  };

  const save = async () => {
    try {
      setSaving(true);
      setError('');
      setSettings(await updatePartnerQuoteSettings(partner.id, settings));
      setSuccess('Conditions commerciales enregistrées. Elles pourront être proposées lors de la création d’un devis.');
    } catch (requestError) {
      setError(requestError.message || 'Impossible d’enregistrer les conditions commerciales.');
    } finally {
      setSaving(false);
    }
  };

  const fieldProps = (field) => ({
    name: field,
    value: settings[field],
    onChange: (event) => update(field, event.target.value),
    disabled: saving,
    fullWidth: true,
  });

  if (loading) return <section className="partner-section"><p className="partner-empty">Chargement des conditions commerciales…</p></section>;

  return (
    <section className="partner-section partner-quote-settings">
      <header className="partner-quote-settings__header">
        <div>
          <Typography variant="h6" component="h2">Conditions de devis</Typography>
          <p>Valeurs proposées lors de la création d’un devis pour ce partenaire. Le devis conserve ensuite sa propre copie indépendante.</p>
        </div>
        <Checkbox
          className="partner-quote-settings__toggle"
          label="Utiliser ces conditions"
          checked={settings.enabled}
          disabled={saving}
          onChange={(event) => update('enabled', event.target.checked)}
        />
      </header>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <section className="partner-quote-settings__group" aria-labelledby="partner-quote-commercial-title">
        <Typography variant="subtitle2" component="h3" id="partner-quote-commercial-title">Conditions commerciales</Typography>
        <div className="partner-quote-settings__grid">
          <div className="partner-quote-settings__inline">
            <Input label="Remise globale négociée" type="number" min="0" step="0.01" {...fieldProps('globalDiscountValue')} />
            <Select label="Unité" options={[{ value: 'percent', label: '%' }, { value: 'amount', label: '€' }]} {...fieldProps('globalDiscountKind')} />
          </div>
          <Input label="Délai de paiement (jours)" type="number" min="0" {...fieldProps('paymentDelayDays')} />
          <Input label="Acompte (%)" type="number" min="0" max="100" step="0.01" {...fieldProps('depositRate')} />
          <Input label="Validité par défaut (jours)" type="number" min="0" {...fieldProps('validityDays')} />
          <Input label="TVA par défaut (%)" type="number" min="0" step="0.01" {...fieldProps('defaultVatRate')} />
          <Select label="Devise" options={['EUR', 'CHF', 'GBP', 'USD'].map((value) => ({ value, label: value }))} {...fieldProps('currencyCode')} />
          <Input className="partner-quote-settings__wide" label="Conditions de paiement" multiline rows={3} {...fieldProps('paymentTerms')} />
        </div>
      </section>

      <section className="partner-quote-settings__group" aria-labelledby="partner-quote-coefficients-title">
        <Typography variant="subtitle2" component="h3" id="partner-quote-coefficients-title">Remise matériel et coefficients</Typography>
        <p className="partner-quote-settings__hint">Laissez un coefficient vide pour utiliser le référentiel par défaut.</p>
        <div className="partner-quote-settings__grid">
          <Input label="Remise achat matériel négociée (%)" type="number" min="0" max="100" step="0.01" {...fieldProps('materialPurchaseDiscountRate')} />
          {coefficientFields.map(([field, label]) => (
            <Input key={field} label={label} type="number" min="0" step="0.01" placeholder="Référentiel par défaut" {...fieldProps(field)} />
          ))}
        </div>
      </section>

      <section className="partner-quote-settings__group">
        <Input label="Notes internes" multiline rows={3} {...fieldProps('notes')} />
      </section>

      <footer className="partner-quote-settings__footer">
        {settings.updatedAt && <small>Dernière mise à jour : {new Date(settings.updatedAt).toLocaleString('fr-FR')}</small>}
        <Button variant="primary" loading={saving} onClick={save}>{saving ? 'Enregistrement…' : 'Enregistrer les conditions'}</Button>
      </footer>
    </section>
  );
}
